import { AfterViewInit, ChangeDetectorRef, Component, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { NgbToastModule } from '@ng-bootstrap/ng-bootstrap';
import { filter } from 'rxjs';
import { ToastService } from './core/services/toast.service';
import { LoaderService } from './core/services/loader.service';
import { LoaderComponent } from './shared/components/loader/loader.component';

// External globals loaded via <script> tags / jQuery plugins (ported from custom.js)
declare const bootstrap: any;
declare const flatpickr: any;
declare const $: any;

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, NgbToastModule, LoaderComponent],
  templateUrl: './app.html',
  styleUrl: './app.scss',
})
export class App implements AfterViewInit {
  constructor(
    public toastService: ToastService,
    public loader: LoaderService,
    private cdr: ChangeDetectorRef,
    private router: Router,
  ) {
    this.toastService.registerChangeDetector(this.cdr);

    // The SVG images live inside lazy-loaded route components that mount into
    // <router-outlet> after the root view has initialized. Re-run the inline-SVG
    // swap after every navigation, once the newly-activated view is in the DOM.
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe(() => {
        requestAnimationFrame(() => this.replaceSvgImages());
      });
  }

  protected readonly title = signal('Super Admin');

  // Angular equivalent of jQuery's $(document).ready(...)
  ngAfterViewInit(): void {
    this.initProfilePanelToggle();
    this.initRolePermissionMatrix();
    this.replaceSvgImages();
    this.initCustomScrollbar();
    this.initMatchHeight();
    this.initTooltips();
    this.initRoleSwitcher();
    this.initPasswordToggle();
    this.initLoginFormValidation();
    this.initFlatpickr();
  }

  private initProfilePanelToggle(): void {
    const actionButtons = document.querySelectorAll<HTMLElement>('[data-ma-profile-panel-target]');

    if (!actionButtons.length) {
      return;
    }

    const detailsPanel = document.getElementById('maProfileDetailsPanel');
    const passwordPanel = document.getElementById('maProfilePasswordPanel');
    const panelTitle = document.getElementById('maProfilePanelTitle');

    if (!detailsPanel || !passwordPanel || !panelTitle) {
      return;
    }

    const setActivePanel = (target: string | null): void => {
      const showPassword = target === 'password';

      detailsPanel.classList.toggle('d-none', showPassword);
      passwordPanel.classList.toggle('d-none', !showPassword);
      panelTitle.textContent = showPassword ? 'Change Password' : 'Profile Details';

      actionButtons.forEach((button) => {
        const isActive = button.getAttribute('data-ma-profile-panel-target') === target;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-selected', isActive ? 'true' : 'false');

        if (button.classList.contains('btn-outline-primary')) {
          button.classList.toggle('btn-primary', isActive);
          button.classList.toggle('btn-outline-primary', !isActive);
        }
      });
    };

    actionButtons.forEach((button) => {
      button.addEventListener('click', () => {
        setActivePanel(button.getAttribute('data-ma-profile-panel-target'));
      });
    });

    if (window.location.hash === '#change-password') {
      setActivePanel('password');
    }
  }

  private initRolePermissionMatrix(): void {
    const matrixTables = document.querySelectorAll('.ma-role-permission-table');

    if (!matrixTables.length) {
      return;
    }

    matrixTables.forEach((table) => {
      const rowSelectAllBoxes = table.querySelectorAll<HTMLInputElement>('[data-ma-role-select-all-row]');

      rowSelectAllBoxes.forEach((selectAllBox) => {
        const row = selectAllBox.closest('tr');

        if (!row) {
          return;
        }

        const permissionBoxes = row.querySelectorAll<HTMLInputElement>('[data-ma-role-permission]');

        const syncSelectAllState = (): void => {
          const allChecked =
            permissionBoxes.length > 0 &&
            Array.prototype.every.call(permissionBoxes, (checkbox: HTMLInputElement) => checkbox.checked);
          selectAllBox.checked = allChecked;
        };

        selectAllBox.addEventListener('change', () => {
          permissionBoxes.forEach((checkbox) => {
            checkbox.checked = selectAllBox.checked;
          });
        });

        permissionBoxes.forEach((checkbox) => {
          checkbox.addEventListener('change', syncSelectAllState);
        });

        syncSelectAllState();
      });
    });
  }

  /* ===============================
     Replace SVG
  =============================== */
  private replaceSvgImages(): void {
    // `:not([data-svg-processing])` skips images already being fetched, so a
    // second run (e.g. a fast follow-up navigation) never double-processes one.
    const images = document.querySelectorAll<HTMLImageElement>(
      'img.ma-svg:not([data-svg-processing]), img.am-svg:not([data-svg-processing])',
    );

    images.forEach((img) => {
      const imgURL = img.getAttribute('src');

      if (!imgURL) {
        return;
      }

      // Mark synchronously, before the async fetch, to claim this image.
      img.setAttribute('data-svg-processing', 'true');

      const imgID = img.getAttribute('id');
      const imgClass = img.getAttribute('class');

      fetch(imgURL)
        .then((response) => response.text())
        .then((data) => {
          const svg = new DOMParser().parseFromString(data, 'image/svg+xml').querySelector('svg');

          if (!svg) {
            img.removeAttribute('data-svg-processing');
            return;
          }

          if (imgID) {
            svg.setAttribute('id', imgID);
          }
          if (imgClass) {
            svg.setAttribute('class', imgClass + ' replaced-svg');
          }

          svg.removeAttribute('xmlns:a');
          // Once replaced, the element is a <svg> and no longer matches the
          // `img.ma-svg` selector, so subsequent runs skip it naturally.
          img.replaceWith(svg);
        })
        .catch(() => {
          // Un-claim on failure so a later navigation can retry the fetch.
          img.removeAttribute('data-svg-processing');
        });
    });
  }

  /* ===============================
     Custom Scrollbar (jQuery plugin)
  =============================== */
  private initCustomScrollbar(): void {
    if (window.innerWidth > 1025 && $.fn && $.fn.mCustomScrollbar) {
      $('.ma-custom-scrollbar').mCustomScrollbar({
        theme: 'dark-2',
        scrollInertia: 0,
      });
    }
  }

  /* ===============================
     MatchHeight (jQuery plugin)
  =============================== */
  private initMatchHeight(): void {
    if ($.fn && $.fn.matchHeight) {
      $('.ma-match-height').matchHeight();
    }
  }

  /* ===============================
     Bootstrap Tooltips
  =============================== */
  private initTooltips(): void {
    if (typeof bootstrap !== 'undefined' && bootstrap.Tooltip) {
      document.querySelectorAll('[data-bs-toggle="tooltip"]').forEach((el) => {
        new bootstrap.Tooltip(el);
      });
    }
  }

  /* ===============================
     Login Role Switcher
  =============================== */
  private initRoleSwitcher(): void {
    const roleButtons = document.querySelectorAll<HTMLElement>('.ma-role-switcher .ma-role-btn');

    if (!roleButtons.length) {
      return;
    }

    const submitText = document.querySelector<HTMLElement>('#maSignInBtn span');

    roleButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const role = button.dataset['role'];

        roleButtons.forEach((btn) => {
          btn.classList.remove('active');
          btn.setAttribute('aria-pressed', 'false');
        });

        button.classList.add('active');
        button.setAttribute('aria-pressed', 'true');

        if (submitText) {
          submitText.textContent = 'Sign In as ' + role;
        }
      });
    });
  }

  /* ===============================
     Password Toggle
  =============================== */
  private initPasswordToggle(): void {
    const toggle = document.getElementById('maPasswordToggle');

    if (!toggle) {
      return;
    }

    toggle.addEventListener('click', () => {
      const passwordInput = document.getElementById('maPassword') as HTMLInputElement | null;
      const icon = toggle.querySelector('i');

      if (!passwordInput) {
        return;
      }

      const isPassword = passwordInput.getAttribute('type') === 'password';

      passwordInput.setAttribute('type', isPassword ? 'text' : 'password');
      toggle.setAttribute('aria-pressed', isPassword ? 'true' : 'false');

      if (icon) {
        icon.classList.toggle('fa-eye');
        icon.classList.toggle('fa-eye-slash');
      }
    });
  }

  /* ===============================
     Login Form Validation
  =============================== */
  private initLoginFormValidation(): void {
    const form = document.getElementById('maLoginForm') as HTMLFormElement | null;

    if (!form) {
      return;
    }

    form.addEventListener('submit', (event) => {
      if (!form.checkValidity()) {
        event.preventDefault();
        event.stopPropagation();
      }
      form.classList.add('was-validated');
    });
  }

  /* ===============================
     Flatpickr
  =============================== */
  private initFlatpickr(): void {
    if (typeof flatpickr !== 'undefined') {
      document.querySelectorAll('.ma-date-picker').forEach((el) => {
        flatpickr(el, {
          dateFormat: 'Y-m-d',
          allowInput: true,
        });
      });
    }
  }
}
